import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@follow/ui';
import RentalService from '../../../services/RentalService';
import { AvailableEquipment } from '../../../services/models/rental';

interface RentalEquipmentPickerProps {
  visible: boolean;
  rentalId?: number;
  currentEquipmentId?: number;
  // Whether this rental supports updating its price on swap (NOT_STARTED, Stripe
  // pre-authorized) — gates the "update price" checkbox on a different-priced pick.
  priceEligible?: boolean;
  onClose: () => void;
  onSelect: (equipment: AvailableEquipment, updatePrice: boolean) => void;
}

const formatEuros = (cents: number) => `${(cents / 100).toFixed(2)} €`;

const RentalEquipmentPicker: React.FC<RentalEquipmentPickerProps> = ({
  visible,
  rentalId,
  currentEquipmentId,
  priceEligible,
  onClose,
  onSelect,
}) => {
  const [pendingEquipment, setPendingEquipment] = useState<AvailableEquipment | null>(null);
  const [updatePrice, setUpdatePrice] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['rental-available-equipments', rentalId],
    queryFn: () => RentalService.getAvailableEquipments(rentalId!),
    enabled: visible && rentalId != null,
  });

  useEffect(() => {
    if (visible) {
      setPendingEquipment(null);
      setUpdatePrice(false);
    }
  }, [visible]);

  const currentEquipment = data?.equipments.find((equipment) => equipment.id === currentEquipmentId);
  const equipments = data?.equipments.filter((equipment) => equipment.id !== currentEquipmentId);

  // Same-price (or unpriceable) swaps go through immediately, as before — the
  // confirm step only exists to make a price change an explicit, visible choice.
  const handlePick = (equipment: AvailableEquipment) => {
    const currentPrice = currentEquipment?.priceForRentalDuration;
    const samePrice = currentPrice == null
      || equipment.priceForRentalDuration == null
      || equipment.priceForRentalDuration === currentPrice;
    if (samePrice) {
      onSelect(equipment, false);
      return;
    }
    setPendingEquipment(equipment);
    setUpdatePrice(false);
  };

  return (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all">
                {pendingEquipment ? (
                  <>
                    <Dialog.Title as="h3" className="text-neutral typo-label mb-4">
                      Confirmer le remplacement
                    </Dialog.Title>
                    <div className="flex items-center gap-3 mb-4">
                      <img src={pendingEquipment.imageUrl} alt={pendingEquipment.model} className="h-10 w-10 rounded-md object-cover" />
                      <span className="typo-small text-neutral font-medium">{pendingEquipment.model}</span>
                    </div>
                    <div className="flex flex-col gap-2 py-3 border-t border-b border-neutral-10">
                      <div className="flex justify-between typo-small">
                        <span className="text-neutral-50">Prix actuel</span>
                        <span className="text-neutral">{formatEuros(currentEquipment!.priceForRentalDuration!)}</span>
                      </div>
                      <div className="flex justify-between typo-small">
                        <span className="text-neutral-50">Prix du nouvel équipement</span>
                        <span className="text-neutral">{formatEuros(pendingEquipment.priceForRentalDuration!)}</span>
                      </div>
                    </div>
                    {priceEligible ? (
                      <label className="flex items-center gap-2 typo-small text-neutral cursor-pointer select-none mt-3">
                        <input
                          type="checkbox"
                          checked={updatePrice}
                          onChange={(e) => setUpdatePrice(e.target.checked)}
                          className="h-4 w-4 rounded border-neutral-20"
                        />
                        {`Mettre à jour le prix de la réservation (${formatEuros(pendingEquipment.priceForRentalDuration!)})`}
                      </label>
                    ) : (
                      <p className="typo-small text-neutral-40 mt-3">
                        Le prix de la réservation ne peut pas être modifié pour ce mode de paiement — il restera inchangé.
                      </p>
                    )}
                    <div className="mt-6 flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPendingEquipment(null)}>
                        Retour
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onSelect(pendingEquipment, priceEligible ? updatePrice : false)}
                      >
                        Confirmer
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Dialog.Title as="h3" className="text-neutral typo-label mb-4">
                      Choisir un autre équipement
                    </Dialog.Title>

                    {isFetching ? (
                      <p className="py-8 text-center typo-small text-neutral-40">Chargement...</p>
                    ) : !equipments || equipments.length === 0 ? (
                      <p className="py-8 text-center typo-small text-neutral-40">
                        Aucun équipement disponible
                      </p>
                    ) : (
                      <ul className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                        {equipments.map((equipment) => {
                          const currentPrice = currentEquipment?.priceForRentalDuration;
                          const priceDiffers = currentPrice != null
                            && equipment.priceForRentalDuration != null
                            && equipment.priceForRentalDuration !== currentPrice;
                          return (
                          <li key={equipment.id}>
                            <button
                              type="button"
                              onClick={() => handlePick(equipment)}
                              className="w-full flex items-center gap-3 p-2 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 hover:bg-neutral-5 transition-colors text-left"
                            >
                              <img src={equipment.imageUrl} alt={equipment.model} className="h-10 w-10 rounded-md object-cover" />
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="typo-small text-neutral font-medium">{equipment.model}</span>
                                {equipment.lock && (
                                  <span className="typo-caption text-neutral-40">Lock L{equipment.lock.position + 1}</span>
                                )}
                              </div>
                              {priceDiffers && (
                                <span className="typo-caption text-neutral-40 flex-shrink-0">
                                  {formatEuros(equipment.priceForRentalDuration!)}
                                </span>
                              )}
                            </button>
                          </li>
                          );
                        })}
                      </ul>
                    )}

                    <div className="mt-6 flex justify-end">
                      <Button variant="outline" size="sm" onClick={onClose}>
                        Annuler
                      </Button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default RentalEquipmentPicker;
